/**
 * Contract mutation hooks using generic factory
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { contractApi } from '@/lib/api/contracts.api';
import { queryKeys } from '../query-keys';
import { createResourceMutations } from './createResourceMutations';
import { toast } from 'sonner';
import type {
  Contract,
  CreateContract,
  UpdateContract,
  CreateContractChangeOrderInput,
  UpdateContractChangeOrderInput,
  GenerateContractBillingSchedule,
} from '@scrambl/shared';

const mutations = createResourceMutations<Contract, CreateContract, UpdateContract>({
  resourceName: 'Contract',
  resourceNameSingular: 'contract',
  queryKeyFactory: queryKeys.contracts,
  api: {
    create: (data: CreateContract) => contractApi.createContract(data),
    update: (id: string, data: UpdateContract) => contractApi.updateContract(id, data),
    updateField: (id: string, field: string, value: unknown) => {
      const updateData = { [field]: value } as UpdateContract;
      return contractApi.updateContract(id, updateData);
    },
    delete: (id: string) => contractApi.deleteContract(id),
  },
  enableRealtime: false,
});

export const useCreateContractMutation = mutations.useCreateMutation;
export const useUpdateContractMutation = mutations.useUpdateMutation;
export const useUpdateContractFieldMutation = mutations.useUpdateFieldMutation!;
export const useDeleteContractMutation = mutations.useDeleteMutation;

/**
 * U5: record a change order — applies the signed delta to the contract's
 * current value and journals the entry. Baseline stays fixed.
 */
export function useCreateContractChangeOrderMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, data }: { contractId: string; data: CreateContractChangeOrderInput }) =>
      contractApi.createChangeOrder(contractId, data),
    onSuccess: (_result, { contractId }) => {
      toast.success('Change order recorded');
      // The POST amends the contract's current value — refresh the detail
      // (which prefixes the change-orders key) and any list rows.
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.detail(contractId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.lists() });
    },
    onError: (error) => {
      toast.error('Failed to record change order');
      console.error('Create contract change order error:', error);
    },
  });
}

/**
 * U5: toggle a change order's billed state / edit its reason (delta immutable).
 */
export function useUpdateContractChangeOrderMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      contractId,
      changeOrderId,
      data,
    }: {
      contractId: string;
      changeOrderId: string;
      data: UpdateContractChangeOrderInput;
    }) => contractApi.updateChangeOrder(contractId, changeOrderId, data),
    onSuccess: (_result, { contractId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.changeOrders(contractId) });
    },
    onError: (error) => {
      toast.error('Failed to update change order');
      console.error('Update contract change order error:', error);
    },
  });
}

/**
 * Materialize recurring billing schedule lines from a contract's billing terms.
 * commit=false is a dry-run preview (no cache changes worth invalidating);
 * commit=true reconciles the schedule, so refresh the contract summary + lines.
 */
export function useGenerateContractBillingScheduleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    // The caller surfaces the specific server message (e.g. "set a period count"),
    // so opt out of the global mutation-error toast to avoid stacking duplicates.
    meta: { silent: true },
    mutationFn: ({
      contractId,
      data,
    }: {
      contractId: string;
      data: GenerateContractBillingSchedule;
    }) => contractApi.generateBillingSchedule(contractId, data),
    onSuccess: (result, { contractId }) => {
      if (!result.committed) return;
      queryClient.invalidateQueries({
        queryKey: ['commercial-ops', 'contracts', contractId, 'summary'],
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.detail(contractId) });
      queryClient.invalidateQueries({ queryKey: ['billing-schedule-lines'] });
    },
    onError: (error) => {
      console.error('Generate contract billing schedule error:', error);
    },
  });
}

/**
 * Bulk delete contracts mutation hook
 */
export function useBulkDeleteContractsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => contractApi.bulkDeleteContracts(ids),
    onSuccess: (result) => {
      toast.success(`${result.deletedCount} contract${result.deletedCount === 1 ? '' : 's'} deleted`);
      // Invalidate contracts list
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.lists() });
    },
    onError: (error) => {
      toast.error('Failed to delete contracts');
      console.error('Bulk delete contracts error:', error);
    },
  });
}
